import { Add, CheckCircle, Warning } from '@mui/icons-material';
import { FormControl, MenuItem, useTheme } from '@mui/material';
import React, { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { MatterStatus } from '~/gql/graphql';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { User } from '~/shared/types/connect';
import { getMatterDisplayHeading } from '~/shared/utils/matterDisplay';
import { useMatterUnreadCounts } from '~/hooks/useMatterUnreadCounts';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import {
  CardActionButton,
  Icon16,
  InlineBadge,
  StyledMatterStatusSelect,
} from '~/modules/matters/MatterCard.styles';
import { getMatterStatusColor } from '~/modules/matters/MatterStatusChip';
import type { Props } from '~/modules/matters/MatterCard.types';
const NEXT_DEADLINE_LABEL = 'Next deadline';
const MATTER_CLOSED_LABEL = 'Matter closed';

function isMatterStatus(value: unknown): value is MatterStatus {
  return (
    typeof value === 'string' &&
    (Object.values(MatterStatus) as string[]).includes(value)
  );
}

const MAX_EXPERTS_VISIBLE = 3;

export interface MatterCardActionsState {
  displayStatus: MatterViewModel['status'];
  descriptionText: string;
  isClosed: boolean;
  isInactiveTab: boolean;
  hasNextDeadline: boolean;
  isOnHold: boolean;
  otherPartyDisplayName: string;
  showExpertsInFooter: boolean;
  visibleExperts: User[];
  remainingExpertCount: number;
  hasDesktopActions: boolean;
  retainerStatusMeta: {
    message: string;
    color: 'red' | 'warning' | 'success';
    icon: ReactNode;
  } | null;
  statusBadge: ReactNode;
  leftBorderVariant: 'orange' | 'red' | 'green' | 'gray';
  matterHeading: string;
  getReopenButton: (mobile: boolean) => ReactNode;
  getRequestRetainerButton: (mobile: boolean) => ReactNode;
  getRequestMoreButton: (mobile: boolean) => ReactNode;
  getViewRetainerButton: (mobile: boolean) => ReactNode;
  getAcceptRequestButton: (mobile: boolean) => ReactNode;
  mobileReopen: () => ReactNode;
  mobileAccept: () => ReactNode;
  mobileRequestMore: () => ReactNode;
  mobileViewRetainer: () => ReactNode;
  mobileRequestRetainer: () => ReactNode;
  navigateToChat: (matterId: string) => void;
  unreadMessageCount: number;
  messageLabel: string;
  statusDropdown: ReactNode;
}

interface GetStatusBadgeNodeOptions {
  isClosed: boolean;
  isInactiveTab: boolean;
  isOnHold: boolean;
  hasNextDeadline: boolean;
  matterClosedAt: string | null | undefined;
  matterUpdatedAt: string | null | undefined;
  matterNextDeadline: string | null | undefined;
}

function getStatusBadgeNode(options: GetStatusBadgeNodeOptions): ReactNode {
  const {
    isClosed,
    isInactiveTab,
    isOnHold,
    hasNextDeadline,
    matterClosedAt,
    matterUpdatedAt,
    matterNextDeadline,
  } = options;
  if (isClosed) {
    let closedDate: Date | null = null;
    if (matterClosedAt) {
      closedDate = new Date(matterClosedAt);
    } else if (matterUpdatedAt) {
      closedDate = new Date(matterUpdatedAt);
    }
    const closedLabel =
      closedDate && isValid(closedDate)
        ? format(closedDate, 'MMM d, yyyy')
        : '—';
    return (
      <InlineBadge>
        {MATTER_CLOSED_LABEL}: {closedLabel}
      </InlineBadge>
    );
  }
  if (isInactiveTab || isOnHold) {
    return <InlineBadge>Placed on hold</InlineBadge>;
  }
  if (hasNextDeadline && matterNextDeadline) {
    const nextDeadlineDate = new Date(matterNextDeadline);
    const nextDeadlineLabel = isValid(nextDeadlineDate)
      ? formatInTimeZone(nextDeadlineDate, 'UTC', 'MMM d, yyyy')
      : '—';
    return (
      <InlineBadge>
        {NEXT_DEADLINE_LABEL}: {nextDeadlineLabel}
      </InlineBadge>
    );
  }
  return <InlineBadge>No deadline set</InlineBadge>;
}

function getLeftBorderVariant(
  closed: boolean,
  funded: boolean,
  pending: boolean,
): 'orange' | 'red' | 'green' | 'gray' {
  if (closed) {
    return 'gray';
  }
  if (funded) {
    return 'green';
  }
  if (pending && !funded) {
    return 'red';
  }
  return 'orange';
}

export function useMatterCardActions(props: Props): MatterCardActionsState {
  const {
    matter,
    otherParty,
    hasPendingRetainer,
    hasFundedRetainer,
    activeRetainerCount = 0,
    isAttorneyView,
    matterTab,
    experts,
    onRequestRetainer,
    onReopenMatter,
    onAcceptRetainerRequest,
    onStatusChange,
  } = props;

  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useCurrentUser();
  const { total: unreadMessageCount } = useMatterUnreadCounts({
    matterId: matter.id,
    skip: !matter.id,
  });

  const displayStatus = matter.status;
  const descriptionText = matter.caseDescription.trim();

  const isClosed = displayStatus === MatterStatus.Closed;
  const isInactiveTab = matterTab === 'inactive';
  const hasNextDeadline = Boolean(matter.nextDeadline);
  const isOnHold =
    displayStatus === MatterStatus.Inactive &&
    !hasPendingRetainer &&
    !hasFundedRetainer;
  const otherPartyDisplayName = otherParty?.name ?? '';
  const hasMultipleExperts = (experts?.length ?? 0) > 1;
  const showExpertsInFooter = Boolean(experts?.length);
  const visibleExperts =
    showExpertsInFooter && experts ? experts.slice(0, MAX_EXPERTS_VISIBLE) : [];
  const remainingExpertCount =
    showExpertsInFooter && experts
      ? Math.max(0, experts.length - MAX_EXPERTS_VISIBLE)
      : 0;

  const expertRetainerActionsAllowed = Boolean(
    currentUser?.stripeConnectOnboarded,
  );

  const canReopen = isClosed && Boolean(onReopenMatter);
  const canAcceptRetainer =
    !isClosed &&
    hasPendingRetainer &&
    isAttorneyView &&
    Boolean(onAcceptRetainerRequest);
  const canOtherPartyInitiateRetainer =
    !isClosed &&
    !isAttorneyView &&
    Boolean(otherParty) &&
    !hasPendingRetainer &&
    !hasFundedRetainer &&
    expertRetainerActionsAllowed;
  const hasFunded = !isClosed && hasFundedRetainer;
  const hasDesktopActions = Boolean(
    canReopen ||
      canAcceptRetainer ||
      canOtherPartyInitiateRetainer ||
      hasFunded,
  );

  let retainerStatusMeta: MatterCardActionsState['retainerStatusMeta'] = null;
  if (!isClosed) {
    if (hasPendingRetainer && !hasFundedRetainer) {
      retainerStatusMeta = {
        message: isAttorneyView
          ? 'Pending Approval'
          : 'Pending attorney approval & pay',
        color: 'red',
        icon: <Warning aria-hidden />,
      };
    } else if (!hasPendingRetainer && !hasFundedRetainer) {
      const hasExpertAssigned =
        Boolean(otherParty) || (experts?.length ?? 0) > 0;
      if (isAttorneyView && !hasExpertAssigned) {
        retainerStatusMeta = null;
      } else {
        retainerStatusMeta = {
          message: isAttorneyView
            ? 'Waiting for expert to request retainer'
            : 'Pending to request retainer',
          color: 'warning',
          icon: <Warning aria-hidden />,
        };
      }
    } else if (hasFundedRetainer) {
      retainerStatusMeta = {
        message: isAttorneyView ? 'Active retainer' : 'Retainer funded',
        color: 'success',
        icon: <CheckCircle aria-hidden />,
      };
    }
  }

  const statusBadge = getStatusBadgeNode({
    isClosed,
    isInactiveTab,
    isOnHold,
    hasNextDeadline,
    matterClosedAt: matter.closedAt,
    matterUpdatedAt: matter.updatedAt,
    matterNextDeadline: matter.nextDeadline,
  });

  const statusColors = getMatterStatusColor(
    theme,
    displayStatus ?? MatterStatus.Inactive,
  );
  const statusDropdown: ReactNode =
    onStatusChange && displayStatus !== null ? (
      <FormControl
        size="small"
        sx={{ minWidth: 120 }}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <StyledMatterStatusSelect
          value={displayStatus}
          onChange={(event) => {
            const value = event.target.value;
            if (isMatterStatus(value)) {
              onStatusChange(matter, value);
            }
          }}
          displayEmpty
          $bgcolor={statusColors.bgcolor}
          $color={statusColors.color}
          inputProps={{ 'aria-label': 'Matter status' }}
        >
          <MenuItem value={MatterStatus.Active}>Active</MenuItem>
          <MenuItem value={MatterStatus.Inactive}>Inactive</MenuItem>
          <MenuItem value={MatterStatus.Closed}>Closed</MenuItem>
        </StyledMatterStatusSelect>
      </FormControl>
    ) : null;

  const leftBorderVariant = getLeftBorderVariant(
    isClosed,
    hasFundedRetainer,
    hasPendingRetainer,
  );

  const matterHeading = getMatterDisplayHeading(matter, {
    stripPotentialLabel: displayStatus === MatterStatus.Inactive,
  });

  function getReopenButton(mobile: boolean): ReactNode {
    if (!isClosed || !onReopenMatter) {
      return null;
    }
    return (
      <CardActionButton
        variant="outlined"
        size="small"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onReopenMatter(matter);
        }}
        $mobile={mobile}
      >
        Reopen
      </CardActionButton>
    );
  }

  function getRequestRetainerButton(mobile: boolean): ReactNode {
    if (!canOtherPartyInitiateRetainer) {
      return null;
    }
    return (
      <CardActionButton
        variant="outlined"
        size="small"
        startIcon={
          displayStatus !== MatterStatus.Inactive ? (
            <Icon16 as="span">
              <Add aria-hidden />
            </Icon16>
          ) : undefined
        }
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          if (onRequestRetainer) {
            onRequestRetainer(matter);
          } else {
            navigate(`/matters/${matter.id}`);
          }
        }}
        $mobile={mobile}
      >
        {displayStatus === MatterStatus.Inactive
          ? 'Activate matter'
          : 'Request Retainer'}
      </CardActionButton>
    );
  }

  function getRequestMoreButton(mobile: boolean): ReactNode {
    if (
      isClosed ||
      !hasFundedRetainer ||
      isAttorneyView ||
      hasPendingRetainer ||
      matterTab === 'inactive' ||
      !expertRetainerActionsAllowed
    ) {
      return null;
    }
    return (
      <CardActionButton
        variant="outlined"
        size="small"
        startIcon={
          <Icon16 as="span">
            <Add aria-hidden />
          </Icon16>
        }
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          if (onRequestRetainer) {
            onRequestRetainer(matter);
          } else {
            navigate(`/matters/${matter.id}`);
          }
        }}
        $mobile={mobile}
      >
        Request More
      </CardActionButton>
    );
  }

  function getViewRetainerButton(mobile: boolean): ReactNode {
    if (isClosed || !hasFundedRetainer || isAttorneyView) {
      return null;
    }
    const label = activeRetainerCount > 1 ? 'View Retainers' : 'View Retainer';
    return (
      <CardActionButton
        variant="outlined"
        size="small"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          navigate(`/matters/${matter.id}`);
        }}
        $mobile={mobile}
      >
        {label}
      </CardActionButton>
    );
  }

  function getAcceptRequestButton(mobile: boolean): ReactNode {
    if (
      isClosed ||
      !hasPendingRetainer ||
      !isAttorneyView ||
      !onAcceptRetainerRequest
    ) {
      return null;
    }
    const buttonLabel = 'Approve & Fund';
    return (
      <CardActionButton
        variant="outlined"
        size="small"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onAcceptRetainerRequest(matter);
        }}
        $mobile={mobile}
      >
        {buttonLabel}
      </CardActionButton>
    );
  }

  const navigateToChat = (matterId: string) => {
    if (hasMultipleExperts) {
      navigate(`/matters/${matterId}?tab=experts`);
    } else {
      navigate(`/matters/${matterId}?tab=experts&chat=true`);
    }
  };

  return {
    displayStatus,
    descriptionText,
    isClosed,
    isInactiveTab,
    hasNextDeadline,
    isOnHold,
    otherPartyDisplayName,
    showExpertsInFooter,
    visibleExperts,
    remainingExpertCount,
    hasDesktopActions,
    retainerStatusMeta,
    statusBadge,
    leftBorderVariant,
    matterHeading,
    getReopenButton,
    getRequestRetainerButton,
    getRequestMoreButton,
    getViewRetainerButton,
    getAcceptRequestButton,
    mobileReopen: () => getReopenButton(true),
    mobileAccept: () => getAcceptRequestButton(true),
    mobileRequestMore: () => getRequestMoreButton(true),
    mobileViewRetainer: () => getViewRetainerButton(true),
    mobileRequestRetainer: () => getRequestRetainerButton(true),
    navigateToChat,
    unreadMessageCount,
    messageLabel: hasMultipleExperts ? 'Messages' : 'Message',
    statusDropdown,
  };
}
